import React, { useContext, useEffect, useMemo } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import styles from '../../../../../styles/components/Modal/Employees/components/PermissionGroupTab.module.css';
import { LangContext } from '../../../../../context/LangContext';
import { getPermissionDescription, getPermissionLabel } from '../../../../../utils/translations/permissionTranslations';

const translations = {
    ru: {
        title: 'Все права',
        subtitle: 'Справочный список всех доступных прав в системе. Это окно только для просмотра.',
        close: 'Закрыть',
        empty: 'Доступные права не найдены.',
    },
    en: {
        title: 'All Permissions',
        subtitle: 'Reference list of all available system permissions. This window is view-only.',
        close: 'Close',
        empty: 'No available permissions found.',
    },
};

export const AllPermissionsModal = ({
    isOpen,
    onClose,
    groups,
    title,
    subtitle,
    isDark,
}) => {
    const { langSelected } = useContext(LangContext);
    const t = translations[langSelected] || translations.ru;

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    const visibleGroups = useMemo(
        () => (Array.isArray(groups) ? groups.filter((group) => Array.isArray(group.permissions) && group.permissions.length > 0) : []),
        [groups]
    );

    if (!isOpen) {
        return null;
    }

    return (
        <div
            className={`${styles.permissionsViewerOverlay} ${isDark ? styles.darkPermissionsViewerOverlay : ''}`}
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <div
                className={`${styles.permissionsViewerModal} ${isDark ? styles.darkPermissionsViewerModal : ''}`}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className={`${styles.permissionsViewerHeader} ${isDark ? styles.darkPermissionsViewerHeader : ''}`}>
                    <div>
                        <h3 className={`${styles.permissionsViewerTitle} ${isDark ? styles.darkPermissionsViewerTitle : ''}`}>{title || t.title}</h3>
                        <p className={`${styles.permissionsViewerSubtitle} ${isDark ? styles.darkPermissionsViewerSubtitle : ''}`}>
                            {subtitle || t.subtitle}
                        </p>
                    </div>
                    <button
                        type="button"
                        className={`${styles.permissionsViewerCloseButton} ${isDark ? styles.darkPermissionsViewerCloseButton : ''}`}
                        onClick={onClose}
                        aria-label={t.close}
                    >
                        <CloseIcon fontSize="small" />
                    </button>
                </div>

                <div className={styles.permissionsViewerBody}>
                    {visibleGroups.length === 0 ? (
                        <p className={`${styles.permissionsViewerEmpty} ${isDark ? styles.darkPermissionsViewerEmpty : ''}`}>{t.empty}</p>
                    ) : (
                        visibleGroups.map((group) => (
                            <section
                                key={group.key}
                                className={`${styles.permissionsViewerGroup} ${isDark ? styles.darkPermissionsViewerGroup : ''}`}
                            >
                                <h4 className={`${styles.permissionsViewerGroupTitle} ${isDark ? styles.darkPermissionsViewerGroupTitle : ''}`}>
                                    {group.title?.[langSelected] || group.title?.ru || group.key}
                                </h4>
                                <div className={styles.permissionsViewerList}>
                                    {group.permissions.map((permission) => (
                                        <div
                                            key={permission.name || permission}
                                            className={`${styles.permissionsViewerItem} ${isDark ? styles.darkPermissionsViewerItem : ''}`}
                                        >
                                            <div className={`${styles.permissionsViewerItemTitle} ${isDark ? styles.darkPermissionsViewerItemTitle : ''}`}>
                                                {getPermissionLabel(permission, langSelected)}
                                            </div>
                                            {getPermissionDescription(permission, langSelected) && (
                                                <div className={`${styles.permissionsViewerItemDescription} ${isDark ? styles.darkPermissionsViewerItemDescription : ''}`}>
                                                    {getPermissionDescription(permission, langSelected)}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};
